
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useNotify } from "@/hooks/useNotify";
import { supabase } from "@/integrations/supabase/client";
import { InvitationData, CompanyData } from "../types";

export const useProfileSubmission = (
  invitation: InvitationData | null,
  company: CompanyData | null
) => {
  const [isLoading, setIsLoading] = useState(false);
  const notify = useNotify();
  const navigate = useNavigate();

  const handleProfileSubmit = async (profileData: {
    firstName: string;
    lastName: string;
    companyRole: string;
    personalPhoneNumber: string;
    profilePhotoUrl: string | null;
  }) => {
    if (!invitation || !company) {
      console.error("Missing invitation or company data");
      return;
    }

    try {
      setIsLoading(true);
      console.log("[ProfileSubmission] Starting profile submission process");

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error("No authenticated user found");
      }

      console.log("[ProfileSubmission] Updating user profile");

      // Standard company invitation flow
      const { error: profileError } = await supabase
        .from("profiles")
        .update({
          first_name: profileData.firstName,
          last_name: profileData.lastName,
          company_position: profileData.companyRole,
          personal_phone_number: profileData.personalPhoneNumber,
          profile_photo_url: profileData.profilePhotoUrl,
          company_id: company.id,
          profile_completed: true,
          updated_at: new Date().toISOString()
        })
        .eq("id", user.id);

      if (profileError) {
        console.error("Profile update error:", profileError);
        throw profileError;
      }

      console.log("[ProfileSubmission] Checking if user is already a company member");

      const { data: existingMember, error: checkError } = await supabase
        .from("company_members")
        .select("id")
        .eq("user_id", user.id)
        .eq("company_id", company.id)
        .single();

      if (checkError && checkError.code !== 'PGRST116') {
        console.error("Error checking company membership:", checkError);
        throw checkError;
      }

      if (!existingMember) {
        console.log("[ProfileSubmission] Adding user to company members");

        const { error: memberError } = await supabase
          .from("company_members")
          .insert({
            user_id: user.id,
            company_id: company.id,
            role: "member"
          });

        if (memberError) {
          console.error("Company member addition error:", memberError);
          throw memberError;
        }
      } else {
        console.log("[ProfileSubmission] User is already a company member, skipping insertion");
      }

      console.log("[ProfileSubmission] Updating invitation status");

      const { error: companyInvitationError } = await supabase
        .from("company_invitations")
        .update({ status: "accepted" })
        .eq("id", invitation.id);

      if (companyInvitationError) {
        const { error: legacyInvitationError } = await supabase
          .from("invitations")
          .update({ status: "accepted" })
          .eq("id", invitation.id);

        if (legacyInvitationError) {
          console.error("Invitation update error:", legacyInvitationError);
          throw legacyInvitationError;
        }
      }

      console.log("[ProfileSubmission] All operations completed successfully");
      notify.success(`You've successfully joined ${company.name}. Redirecting to dashboard...`);

      setTimeout(() => {
        console.log("[ProfileSubmission] Redirecting to dashboard with page reload");
        window.location.href = "/dashboard";
      }, 1500);

    } catch (error: any) {
      console.error("Profile submission error:", error);
      notify.error(error.message || "An error occurred while setting up your profile");
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isLoading,
    handleProfileSubmit
  };
};
